// The results page's tabs. The tab bar and the tour share this, so a new tab cannot leave the tour behind.
export type ScenarioAnalysisTab = "scenarios" | "worth-it" | "detailed-calcs";

// Results tour when there is something to compare, empty-state tour when there is not. Each has its own progress.
export type OutcomesTourKind = "results" | "empty";

// Each tab's steps form one chapter. The tour visits them in this order.
export type OutcomesTourChapter = ScenarioAnalysisTab;
export const OUTCOMES_TOUR_CHAPTER_ORDER: readonly OutcomesTourChapter[] = ["worth-it", "detailed-calcs", "scenarios"];

// The page marks each highlightable area with `data-tour-target="<value>"`.
export type OutcomesTourTarget = "comparison" | "chart" | "settings-bar" | "calculations-table" | "edit-values" | "add-scenario" | "current-rotation";

// Keys under `outcomesTour.steps` in the scenarioAnalysis namespace.
export type OutcomesTourStepId =
  | "worthOnYourLand"
  | "yearByYear"
  | "otherMarkets"
  | "whereNumbersComeFrom"
  | "aboutYourFarm"
  | "nextIdea"
  | "nothingToWeighUp"
  | "startWithWhatYouGrow"
  | "buildTheChange";

export interface OutcomesTourStep {
  id: OutcomesTourStepId;
  chapter: OutcomesTourChapter;
  target: OutcomesTourTarget;
  /** Draws the band just outside the area, for controls and tables whose edge holds content. */
  outset?: boolean;
  /** Using the area ends the tour when this is the run's last step. */
  finishesOnUse?: boolean;
}

export const OUTCOMES_TOUR_STEPS: Record<OutcomesTourKind, readonly OutcomesTourStep[]> = {
  results: [
    { id: "worthOnYourLand", chapter: "worth-it", target: "comparison" },
    { id: "yearByYear", chapter: "worth-it", target: "chart", outset: true },
    { id: "otherMarkets", chapter: "worth-it", target: "settings-bar" },
    { id: "whereNumbersComeFrom", chapter: "detailed-calcs", target: "calculations-table", outset: true },
    { id: "aboutYourFarm", chapter: "detailed-calcs", target: "edit-values", outset: true },
    { id: "nextIdea", chapter: "scenarios", target: "add-scenario", outset: true, finishesOnUse: true },
  ],
  empty: [
    { id: "nothingToWeighUp", chapter: "worth-it", target: "comparison" },
    { id: "startWithWhatYouGrow", chapter: "scenarios", target: "current-rotation" },
    { id: "buildTheChange", chapter: "scenarios", target: "add-scenario", outset: true, finishesOnUse: true },
  ],
};

// Below this many plans there is nothing to compare, so the empty-state tour runs.
export const OUTCOMES_TOUR_MIN_PLANS_FOR_RESULTS = 2;

// An automatic start waits this long, so the card lands after the tab has painted.
export const OUTCOMES_TOUR_TAB_START_DELAY_MS = 300;

// How long a step waits for its area to render before showing the card without a highlight.
export const OUTCOMES_TOUR_TARGET_WAIT_MS = 1500;

// How often a step looks for its area, which can render late or remount.
export const OUTCOMES_TOUR_TARGET_POLL_MS = 100;

// How long the card and band take to glide from one area to the next.
export const OUTCOMES_TOUR_GLIDE_MS = 450;

// A scroll counts as finished after this long without a scroll event.
export const OUTCOMES_TOUR_SCROLL_QUIET_MS = 150;

// A touch that moves further than this is a scroll, not a tap outside the tour.
export const OUTCOMES_TOUR_TAP_SLOP_PX = 10;

// Layout in px. The band width is also handed to the SCSS as a CSS variable, so the two cannot drift.
export const OUTCOMES_TOUR_BAND_PX = 6;
export const OUTCOMES_TOUR_GAP_PX = 12;
export const OUTCOMES_TOUR_EDGE_MARGIN_PX = 16;
// The band's corners when drawn outside an area: the library's card radius.
export const OUTCOMES_TOUR_CARD_RADIUS_PX = 12;

// Marks the tour's own card and band, so the overlay watcher ignores their per-frame style writes.
export const OUTCOMES_TOUR_ELEMENT_ATTRIBUTE = "data-outcomes-tour";

// Modals, drawers and dialogs, antd's and the page's own (Add scenario, project entry), which close or hold back the tour. Recheck on antd upgrades.
export const OUTCOMES_TOUR_OVERLAY_SELECTOR = ".ant-modal-wrap, .ant-drawer-open, .scenario-analysis-modal-overlay, [role='dialog'][aria-modal='true']";
// Popups opened from a highlighted area, such as the change picker's options: pressing in one keeps the tour open.
export const OUTCOMES_TOUR_AREA_POPUP_SELECTOR = ".ant-select-dropdown, .ant-dropdown, .ant-popover, .ant-picker-dropdown, .ant-tooltip";
